/**
 * Audio サンプル候補カード — 候補 1 件の表示と個別調整 (Step3 / 計画 8.4.2)。
 *
 * Step3 の候補一覧 (最大 2 列の grid / SBI-008) に並ぶ 1 枚のカード。
 * 列数の切り替えは CSS (`.ssml-sample-list`) だけで行い、本コンポーネントは幅に関与しない。
 *
 * ## 表示する項目
 *
 *   - 候補番号 + Audio タイトル … タイトルは **AI が決める** (例: 候補 1 駅名強調)
 *   - (AI コメントは SBI-010 で非表示にした。API の `comment` は受け取るが画面には出さない。
 *      利用者はタイトルと音声で判断する / 二列表示後のカード高さを抑える)
 *   - SSML           … **常時表示** の編集可能なテキストエリア (UI/UX 改善 Phase2 / 見える「SSML 全文」ラベルは廃止)
 *   - 音声操作列      … [Audio プレイヤー] [変更内容でAudio再作成] [ダウンロード] (SBI-006)
 *       - 変更内容でAudio再作成 … 編集した SSML で音声を作り直す (失敗時の再試行も兼ねる)
 *       - 音声                  … 生成中 / 再生 (`<audio controls>`) / 失敗メッセージ
 *       - ダウンロード          … 再生と **同じ Blob URL** を `<a download>` で保存
 *
 * ## ダウンロード (計画 1.2 / 8.1 の項目 10)
 *
 * ```
 * <a download="遅延案内_駅名強調.mp3" href={audio.url}>  ← 再生と同じ URL
 * ```
 *
 * ⚠️ **`/audio-preview` を呼び直さない**。保存のために再合成すると
 *   Polly の課金が二重に発生し、しかも「再生した音」と「保存した音」が
 *   別の合成結果になる (Polly の出力は毎回ビット単位で同一とは限らない)。
 *   `audio.url` は Blob を指す URL なので、そのまま `<a download>` に渡せば
 *   **再生中のデータがそのまま保存される**。
 *
 * ⚠️ SSML を編集して「Audio 再作成」した場合も、`audio.url` は
 *   `useSsmlAudio` が差し替えた **最新の URL** になっている。
 *   よってダウンロード対象は常に「今再生できる音声」= 最新の音声。
 *
 * ⚠️ 音声が無い状態 (生成中 / 失敗) では **無効化した状態で出す**。
 *   リンク自体を消すと「この候補は保存できない仕様なのか」と分からないため、
 *   押せない理由 (音声が未生成) が伝わる形で残す。
 *
 * ## SSML 編集と Audio 再作成 (計画 8.1 の項目 11・12 / 9.5.4)
 *
 * ```
 * SSML テキストエリアを編集
 *    ↓ [Audio 再作成] 押下
 *    ↓ POST /audio-preview (編集後の SSML のみ)
 *    ↓ 同一カード内の <audio> が差し替わる
 * ```
 *
 * ⚠️ **Bedrock (`/ssml-tuning/samples`) を呼び直さない**。
 *   呼び直すと候補一覧そのものが作り直され、
 *   「他候補の SSML と音声が消える」「編集した SSML が捨てられる」ことになる。
 *   再作成は **編集済み SSML の再合成 (Polly 1 回) だけ**。
 *
 * ⚠️ **履歴 (v1/v2/v3) は残さない** (計画 8.7 / Phase2)。
 *   状態は「現在の SSML と音声」の 1 組のみで、世代の採番を持たない。
 *   編集前に戻したい場合はテキストエリアで Ctrl+Z を使う (計画 0.7-(1))。
 *
 * ## 音声の扱い (計画 9.3.2 / 9.5.4)
 *
 * 音声は「Audio サンプル作成」1 回で候補生成に続けて自動生成される。
 * 本カードは **合成を行わず**、渡された状態 (`audio`) を描画するだけ
 * (合成と blob URL の解放は `useSsmlAudio` に集約 / R10)。
 *
 * ⚠️ **自動再生しない**。候補が複数並ぶため、自動再生すると
 *   複数の音声が同時に鳴って聴き比べにならない。
 *   `<audio controls>` を出し、再生は利用者の操作に委ねる。
 *
 * ## レイアウト (SBI-006 Step3 音声操作 UI 統一)
 *
 * ```
 * ┌ 候補 1  駅名強調 ───────────────────────────────────────────┐
 * │ ┌──────────────────────────────────────────────────────────┐ │
 * │ │ <speak>…</speak>                                         │ │
 * │ └──────────────────────────────────────────────────────────┘ │
 * │ [▶ audio ──────] [変更内容でAudio再作成] [⬇ ダウンロード]  │
 * │ 未反映の注記 / 押せない理由 / エラー                         │
 * └──────────────────────────────────────────────────────────────┘
 * ```
 *
 * Step1 / Step2 (`StepAudioControls`) と同じ「テキスト欄の下に 1 行の操作列」にそろえる。
 * 「SSML を直す → 作り直す → 聴く」の流れを上から下へ追えるようにするため。
 * 操作列の描画は共通コンポーネント `AudioControls` (SBI-009) に任せる。CSS クラスも共有される。
 *
 * ⚠️ SBI-009 で統合したのは **描画だけ**。状態管理は統合しない
 *   (本カードは従来どおり親が持つ音声状態を `AudioControls` へ渡すだけ)。
 * ⚠️ Step3 固有の「候補単位の状態 (props)」「`onDownloaded` (audio_downloaded ログ)」
 *   「再試行と再作成の統合」「押せない理由」「候補番号と testid」は本カードに維持する。
 *
 * ⚠️ SSML 全文は **折りたたまない** (Phase2 で `<details>` を廃止)。
 * 開閉の操作が 1 手増えるうえ、閉じていると「編集できる」ことに気づかれないため。
 * 高さは行数の抑制 (rows=6 + 縦リサイズ可) で吸収する。
 *
 * ## 再試行と再作成のボタンを 1 つにする理由 (UI/UX 改善 Phase2)
 *
 * 従来は「音声を再試行」(失敗時) と「Audio 再作成」(編集時) の 2 つがあったが、
 * どちらも **その候補の現在の SSML で `/audio-preview` を呼び直す** 同じ操作なので、
 * 名称を「変更内容でAudio再作成」に統一して 1 つにまとめる。
 *
 *   - 音声が失敗している候補 … 未編集でも押せる (= 従来の再試行)
 *   - それ以外               … SSML を編集したときだけ押せる (未編集の再合成は課金の無駄)
 */
import type React from 'react';

import type { SsmlSampleCandidate } from '../../api/ssmlTuning';

import { buildAudioFileName } from './fileName';
import { prepareEditedSsml } from './ssmlDraft';
import { AudioControls } from './AudioControls';
import { hasEditedSinceApplied, isAudioSynthesizing } from './audioControlsUtils';
import type { AudioControlsTestIds } from './audioControlsUtils';
import type { SsmlAudioState } from './useSsmlAudio';

export interface SampleCandidateCardProps {
  /** 表示する候補 (タイトル / SSML / AI コメント) */
  candidate: SsmlSampleCandidate;
  /**
   * 候補の並び順 (1 起算)。
   *
   * 見出しに番号を付けて「5 件のうちの何番目か」を示すために使う。
   * タイトルだけでは順序が分からず、口頭で共有しづらいため
   * (「3 番目の候補が良い」と言えるようにする)。
   */
  index: number;
  /**
   * この候補の音声の状態 (`useSsmlAudio` が持つ値)。
   *
   * `undefined` は「まだ音声生成を始めていない」= 音声欄を出さない状態。
   * 候補生成の直後は親が `markPending()` で `pending` を入れるため、
   * 通常は `undefined` にならない (テストで音声なしを描画する場合のみ)。
   */
  audio?: SsmlAudioState;
  /**
   * 失敗した音声を **この候補だけ** 作り直す (計画 9.3.2)。
   *
   * 全体をやり直させると、成功した候補まで再合成されて
   * 無駄な Polly 課金が発生する。失敗した 1 件だけを再試行させる。
   */
  onRetryAudio?: () => void;
  /**
   * 編集中の SSML (テキストエリアの値)。
   *
   * ## ⚠️ カード内に state を持たない理由
   *
   * 編集値の正源は **親 (`Step3SamplePanel`)** に置く。カード側で
   * `useState` に持つと、候補一覧を作り直したときに「前回の編集内容が
   * 新しい候補に残る」事故が起きる (React はキーが同じなら再マウントしない。
   * タイトルは再生成で同じ値になり得る)。
   * 親が候補と同じライフサイクルで持てば、生成のたびに必ず初期化される。
   *
   * 省略時は候補の SSML を **読み取り専用** で表示する
   * (編集を受け取る先が無い状態で編集させると、入力が黙って捨てられる)。
   */
  ssmlDraft?: string;
  /** 編集内容を親へ伝える (`ssmlDraft` と対で渡す) */
  onSsmlDraftChange?: (ssml: string) => void;
  /**
   * 編集した SSML で音声を作り直す (計画 8.1 の項目 12)。
   *
   * ⚠️ 呼ばれた側は **`/audio-preview` だけ** を叩く。
   * Bedrock (`/ssml-tuning/samples`) を呼び直すと候補一覧が作り直され、
   * 他候補の音声と編集内容まで失われる。
   */
  onRecreateAudio?: () => void;
  /**
   * 現在の音声が **どの SSML から作られたか**。
   *
   * 「編集したが再作成していない」状態を示すために使う。
   * これが無いと、利用者は編集後の SSML の音声を聴いているつもりで
   * 編集前の音声を聴き続けることになる (最も気づきにくい誤解)。
   */
  appliedSsml?: string;
  /**
   * 再作成の前段で弾いた理由 (空 SSML など)。
   *
   * ⚠️ **この候補だけ** に表示する。他候補へ影響させない (依頼のエラー方針)。
   * 合成そのものの失敗は `audio.error` 側に出る (発生源が違うので分ける)。
   */
  recreateError?: string | null;
  /**
   * ダウンロードのファイル名に使う **放送テキスト** (Step1 の入力)。
   *
   * `{放送タイトル}_{候補タイトル}.mp3` の前半になる (計画 1.2)。
   * 未解析 / 空の場合は `audio_{候補タイトル}.mp3` になる
   * (名前の組み立ては `fileName.ts` の純関数に任せる)。
   *
   * ⚠️ 候補タイトルは `candidate.title` を使うため受け取らない。
   * 2 経路から渡せるようにすると、カードの表示名と保存名が食い違う
   * (「駅名強調」を聴いたのに別名で保存される) 事故が起きる。
   */
  broadcastText?: string | null;
  /**
   * ダウンロードが押されたことを親へ通知する (利用ログ `audio_downloaded` 用)。
   *
   * ## ⚠️ 保存処理には介入しない
   *
   * 保存そのものは `<a download>` のブラウザ機能が行う。本コールバックは
   * **通知だけ** で、`preventDefault()` も再合成も一切しない。
   * ここで処理を挟むと「ログのために保存が失敗する」最悪の形になる。
   *
   * ## ⚠️ 送信内容は親が組み立てる
   *
   * カードは放送文を知らないため、記録内容の組み立ては
   * 親 (`Step3SamplePanel`) に任せる (カードへ Step1 の入力を渡し始めると
   * 「保存名」以外の依存が増えて責務が曖昧になる)。
   *
   * 省略時は何も通知しない (ログを送らない画面でもそのまま動く)。
   */
  onDownloaded?: () => void;
}

/** 「未編集なので押せない」を表す内部値 (理由文として画面には出さない)。 */
const UNEDITED = '__unedited__';

export function SampleCandidateCard({
  candidate,
  index,
  audio,
  onRetryAudio,
  ssmlDraft,
  onSsmlDraftChange,
  onRecreateAudio,
  appliedSsml,
  recreateError,
  broadcastText,
  onDownloaded,
}: SampleCandidateCardProps): React.JSX.Element {
  /*
   * 編集できるのは「編集値と変更ハンドラの両方」が渡されたときだけ。
   * 片方だけで編集させると入力が黙って捨てられる (押せるが動かない UI)。
   */
  const isEditable = ssmlDraft !== undefined && onSsmlDraftChange !== undefined;
  const ssmlValue = ssmlDraft ?? candidate.ssml;
  /*
   * 合成中は再作成を押させない (同じ候補に二重の Polly 呼び出しを走らせない)。
   * `pending` も含めるのは、一括生成の順番待ち中に押されると
   * 「順番が来た合成」と「再作成」が競合して結果が入れ替わるため。
   */
  const isSynthesizing = isAudioSynthesizing(audio);
  /*
   * 編集済みだが再作成していない状態。
   * ⚠️ 前後の空白差は「編集」と見なさない (改行だけで警告が出ると無視され、
   * 本当に必要なときに読まれなくなる)。
   */
  const isDirty = hasEditedSinceApplied(ssmlValue, appliedSsml) && !isSynthesizing;
  /*
   * 「Audio 再作成」を押せない理由 (押せるなら `null`) / UI/UX 改善 Phase1。
   *
   * 押せるのは「SSML の編集内容があり、再作成できる状態」のときだけ。
   *
   *   1. 合成中 / 順番待ちでない (同一候補の二重合成を防ぐ)
   *   2. SSML が空でも長すぎでもない (送っても 400 になるだけ)
   *   3. 現在の音声の元 SSML から **編集されている**
   *      (未編集で押すと同じ音声を作り直すだけで Polly の課金が無駄になる)
   *
   * ⚠️ 2 の判定は親が送信前に使う `prepareEditedSsml` と **同じ関数** を使う。
   * 別実装にすると「押せるのに親で弾かれる / その逆」がずれて起きる。
   * ⚠️ 失敗した音声の作り直しは「音声を再試行」ボタンが担うため、
   * 未編集の再作成を禁止しても失敗候補を救えなくなることはない。
   */
  const isAudioError = audio?.status === 'error';
  const recreateDisabledReason = ((): string | null => {
    if (isSynthesizing) {
      return '音声の生成が終わると再作成できます。';
    }
    const prepared = prepareEditedSsml(ssmlValue);
    if (!prepared.ok) {
      return prepared.error;
    }
    // appliedSsml が未指定 (音声の元が不明) なら編集済みとみなす。
    const isEdited = appliedSsml === undefined || ssmlValue.trim() !== appliedSsml.trim();
    /*
     * ⚠️ 音声が失敗している候補は **未編集でも押せる** (従来の「音声を再試行」)。
     * 一時的な失敗 (スロットリング等) は SSML を直さなくても作り直せば通るため。
     */
    if (!isEdited && !isAudioError) {
      /*
       * ⚠️ 未編集は押せないが **理由文は出さない** (UI/UX 改善 Phase4)。
       * 旧「SSML を編集すると…を実行できます」は全候補に常時出て縦幅を使っていた。
       * 押せないことはボタンの無効表示で伝わるため、`UNEDITED` で区別して文言を出さない。
       */
      return UNEDITED;
    }
    return null;
  })();
  const recreateDisabled = recreateDisabledReason !== null;
  /**
   * 画面に出す理由文。
   *
   * 最終仕上げで、**未編集** と **合成中** の説明文は出さない
   * (説明より SSML 編集領域を広く使うため)。
   *   - 未編集 … ボタンの無効表示で伝わる
   *   - 合成中 … ボタンの無効表示と、プレイヤー位置の「音声を生成中…」で伝わる
   * 行として出すのは、利用者が直す必要のある理由 (空 / 長すぎ) だけ。
   */
  const visibleRecreateReason =
    recreateDisabledReason === UNEDITED || isSynthesizing ? null : recreateDisabledReason;
  /** ホバーで確認できる理由 (行に出さない理由も含む / 未編集は除く)。 */
  const recreateTitle =
    recreateDisabledReason === UNEDITED ? undefined : (recreateDisabledReason ?? undefined);
  /*
   * 押したときの動作。失敗中は再試行 (`onRetryAudio`) を優先する。
   * 親側で完了サマリの失敗件数を戻す処理が再試行側にしか無いため
   * (どちらも「現在の SSML で `/audio-preview` を呼ぶ」点は同じ)。
   */
  const handleRecreateClick = (): void => {
    if (isAudioError && onRetryAudio !== undefined) {
      onRetryAudio();
      return;
    }
    onRecreateAudio?.();
  };
  const canShowRecreate =
    onRecreateAudio !== undefined || (isAudioError && onRetryAudio !== undefined);
  const recreateReasonId = `ssml-sample-recreate-reason-${index}`;
  /*
   * 保存名は純関数に任せる (`遅延案内_駅名強調.mp3` / 計画 1.2)。
   * ⚠️ 音声の有無に関わらず算出する。無効表示のリンクでも `download` 属性を
   * 出しておけば、「どの名前で保存されるか」を押す前に確認できる。
   */
  const downloadFileName = buildAudioFileName({
    broadcastText,
    candidateTitle: candidate.title,
  });

  /*
   * 操作列の testid (候補番号付き / Step3 固有)。
   * 「生成中」と「未作成」は別の testid で区別する (`pending`)。
   */
  const audioTestIds: AudioControlsTestIds = {
    root: `ssml-sample-audio-${index}`,
    player: `ssml-sample-audio-player-${index}`,
    status: `ssml-sample-audio-status-${index}`,
    pending: `ssml-sample-audio-pending-${index}`,
    recreate: `ssml-sample-recreate-${index}`,
    download: `ssml-sample-audio-download-${index}`,
    dirty: `ssml-sample-dirty-${index}`,
    error: `ssml-sample-audio-error-${index}`,
  };

  /*
   * 操作列を出す条件。音声の状態がある、または再作成できる (= 押せる UI がある) とき。
   * ⚠️ どちらも無い読み取り専用の描画では、「押せるが動かない」UI を置かない。
   */
  const showAudioControls = audio !== undefined || canShowRecreate;

  return (
    <li className="ssml-sample-card" data-testid={`ssml-sample-card-${index}`}>
      {/* 1. 候補名 (番号は装飾ではなく識別子。タイトルが似ていても指し示せるようにする)。 */}
      <div className="ssml-sample-card__header">
        <span className="ssml-sample-card__index">候補 {index}</span>
        <h4 className="ssml-sample-card__title" data-testid={`ssml-sample-title-${index}`}>
          {candidate.title}
        </h4>
      </div>

      {/*
        2. SSML 欄 (常時表示)。
        ⚠️ 折りたたまない (Phase2)。開閉の操作が 1 手増えるうえ、閉じていると
        「編集できる」ことに気づかれないため。
      */}
      <div className="ssml-sample-card__ssml">
        {isEditable ? (
          <>
            <textarea
              className="ssml-sample-card__ssml-editor"
              id={`ssml-sample-ssml-${index}`}
              data-testid={`ssml-sample-ssml-${index}`}
              /*
                ⚠️ 編集は `<textarea>` で行う。等幅フォント + 折り返しは CSS 側で指定する。
                見える「SSML 全文」ラベルは廃止した (直下の textarea 自体が SSML だと分かるため)。
                ただし候補が 5 件並ぶと「どのカードの入力欄か」が支援技術に伝わらなくなるので、
                見えない名前 (aria-label) だけ候補番号つきで残す。
              */
              aria-label={`候補 ${index} SSML`}
              value={ssmlValue}
              rows={6}
              spellCheck={false}
              onChange={(e) => onSsmlDraftChange(e.target.value)}
            />
          </>
        ) : (
          /*
            編集を受け取る先が無い場合は読み取り専用で出す。
            <pre> で改行と空白を保つ (タグの入れ子構造を目で追えるようにする)。
          */
          <pre className="ssml-sample-card__ssml-body" data-testid={`ssml-sample-ssml-${index}`}>
            {candidate.ssml}
          </pre>
        )}
      </div>

      {/*
        4. 操作列: [Audio プレイヤー] [変更内容でAudio再作成] [ダウンロード]
        Step1 / Step2 (`StepAudioControls`) と同じ共通部品 `AudioControls` で描画する
        (SBI-006 / SBI-009)。「SSML を直す → 作り直す → 聴く」の流れを上から下へ追えるよう、
        SSML 欄の **下** に置く。
        ⚠️ 共通化したのは描画だけ。音声の状態・再作成の条件・`audio_downloaded` の通知は本カード (Step3) が持つ。
      */}
      {showAudioControls && (
        <AudioControls
          audio={audio}
          testIds={audioTestIds}
          /*
            ⚠️ 合成中 / 未編集 (失敗時を除く) / 空・長すぎの SSML では押させない
            (条件は `recreateDisabledReason` に集約)。ボタンを出すかどうかも本カードが決める。
          */
          recreate={
            canShowRecreate
              ? {
                  onClick: handleRecreateClick,
                  disabled: recreateDisabled,
                  title: recreateTitle,
                  describedBy: visibleRecreateReason !== null ? recreateReasonId : undefined,
                }
              : null
          }
          /*
            ⚠️ 利用ログ (`audio_downloaded`) の通知だけを渡す。保存処理には介入しない。
            保存名は音声の有無に関わらず算出する。
          */
          download={{ fileName: downloadFileName, onDownloaded }}
          isDirty={canShowRecreate && isDirty}
        >
          {/* 押せない理由 (空・長すぎ / ボタンの aria-describedby の参照先)。 */}
          {canShowRecreate && visibleRecreateReason !== null && (
            <p
              className="ssml-tuning-step__disabled-reason"
              id={recreateReasonId}
              data-testid={`ssml-sample-recreate-reason-${index}`}
            >
              {visibleRecreateReason}
            </p>
          )}

          {/*
            再作成の前段で弾いた理由 (空 SSML など)。
            ⚠️ **この候補のカード内だけ** に出す。他候補の表示には触れない
            (1 件の編集ミスで画面全体がエラー表示になるのを防ぐ)。
          */}
          {recreateError !== undefined && recreateError !== null && (
            <p
              className="ssml-tuning-error"
              role="alert"
              data-testid={`ssml-sample-recreate-error-${index}`}
            >
              {recreateError}
            </p>
          )}
        </AudioControls>
      )}
    </li>
  );
}
